import styled, { css } from "styled-components";
import { motion } from "framer-motion";

// ===== PAGE LAYOUT =====

export const Wrapper = styled(motion.div)`
  margin: 0 auto;
  background-color: black;
`;

export const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--black);
`;

export const HeaderSection = styled.div`
  padding: 10px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 20px;
  }
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 2;
  font-family: Inter;
  font-size: clamp(20px, 3.5vmax, 40px);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

export const HeaderBar = styled.div`
  width: 100%;
  height: 8vw;
  max-height: 60px;
  background: var(--grey);
  position: relative;
  border-top: 2px solid;
  border-bottom: 2px solid;
  border-image-slice: 1;
  border-image-source: linear-gradient(
    to right,
    ${(props) => props.from || "magenta"},
    ${(props) => props.to || "cyan"}
  );
  flex-shrink: 0;
`;

// ===== SPOTLIGHT AREA =====

export const SpotlightSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 5vw;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${(props) =>
      props.tint
        ? `radial-gradient(ellipse at 30% 50%, ${props.tint}12 0%, transparent 70%)`
        : "none"};
    transition: background 0.8s ease;
    pointer-events: none;
  }

`;

export const SpotlightContainer = styled(motion.div)`
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 40px;
  position: absolute;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 0 10px;
  }
`;

export const HeroImageContainer = styled.div`
  flex: 0 0 30%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    width: 45%;
    max-width: 200px;
  }
`;

export const HeroImage = styled.img`
  width: 100%;
  max-width: 260px;
  border-radius: 20px;
  box-shadow: 0 0 30px ${(props) => props.accent || "#5c5c5c"}33,
    0 0 60px ${(props) => props.accent || "#5c5c5c"}11;
  transition: box-shadow 0.5s ease;
`;

export const GradientPlaceholder = styled.div`
  width: 100%;
  max-width: 260px;
  aspect-ratio: 1;
  border-radius: 20px;
  background: ${(props) => props.gradient || "#1a1a1a"};
  box-shadow: 0 0 30px ${(props) => props.accent || "#5c5c5c"}33,
    0 0 60px ${(props) => props.accent || "#5c5c5c"}11;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Inter;
  font-size: clamp(24px, 3vw, 40px);
  font-weight: bold;
  color: white;
  opacity: 0.3;
  text-align: center;
  padding: 20px;
`;

export const SpotlightDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

export const ProjectTitle = styled.h1`
  font-family: Inter;
  font-size: clamp(24px, 3.5vw, 44px);
  font-weight: bold;
  margin: 0;
  background-image: linear-gradient(
    to bottom right,
    ${(props) => props.from || "magenta"},
    ${(props) => props.to || "cyan"}
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
`;

export const ProjectDescription = styled.p`
  font-family: Outfit;
  font-size: clamp(13px, 1.3vw, 18px);
  font-weight: 300;
  color: #fcfafb;
  opacity: 0.85;
  margin: 0;
  line-height: 1.6;
`;

// ===== TECH TAGS =====

export const TechTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const TechTag = styled.span`
  font-family: Outfit;
  font-size: clamp(10px, 0.9vw, 13px);
  font-weight: 400;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid ${(props) => props.accent || "#5c5c5c"}88;
  background: ${(props) => props.accent || "#5c5c5c"}15;
  white-space: nowrap;
  transition: border-color 0.3s ease, background 0.3s ease;
`;

// ===== LEARNING TARGETS =====

export const LearningTargetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

export const LearningTargetLabel = styled.h3`
  font-family: Inter;
  font-size: clamp(12px, 1vw, 15px);
  font-weight: 600;
  color: white;
  opacity: 0.6;
  margin: 0;
`;

export const LearningTargetRow = styled.span`
  font-family: Outfit;
  font-size: clamp(11px, 0.9vw, 14px);
  font-weight: 300;
  color: #fcfafb;
  opacity: 0.7;
`;

export const ViewProjectLink = styled.a`
  font-family: Outfit;
  font-size: clamp(14px, 1.2vw, 18px);
  font-weight: 500;
  text-decoration: none;
  color: white;
  opacity: 0.9;
  transition: opacity 0.2s;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  background-image: linear-gradient(
    to right,
    ${(props) => props.from || "magenta"},
    ${(props) => props.to || "cyan"}
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;

  &:hover {
    opacity: 0.7;
  }
`;


// ===== STRIP DIVIDER =====

export const StripDivider = styled.hr`
  width: 100%;
  margin: 0;
  border: none;
  height: 2px;
  flex-shrink: 0;
  background: linear-gradient(
    to right,
    ${(props) => props.from || "magenta"},
    ${(props) => props.to || "cyan"}
  );
  opacity: 0.5;
  transition: background 0.8s ease;
`;

// ===== CAROUSEL STRIP =====

export const StripSection = styled.div`
  height: 170px;
  min-height: 170px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;
  padding: 0 20px;

  @media (max-width: 768px) {
    height: 140px;
    min-height: 140px;
  }

  @media (max-width: 480px) {
    height: 120px;
    min-height: 120px;
    padding: 0 12px;
  }
`;

export const StripTrack = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 10px 0;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

// ===== CAROUSEL CARD =====

const selectedCardStyles = css`
  border-color: ${(props) => props.accent || "#5c5c5c"};
  box-shadow: 0 0 20px ${(props) => props.accent || "#5c5c5c"}44,
    0 0 40px ${(props) => props.accent || "#5c5c5c"}11;
  opacity: 1;
  transform: scale(1.1);
`;

const unselectedCardStyles = css`
  border-color: transparent;
  box-shadow: none;
  opacity: 0.5;
  transform: scale(1);

  &:hover {
    opacity: 0.75;
  }
`;

export const CardContainer = styled(motion.div)`
  flex-shrink: 0;
  width: 130px;
  height: 130px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${(props) => props.bg || "#0d0d0d"};
  border: 2px solid transparent;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  user-select: none;

  ${(props) => (props.isSelected ? selectedCardStyles : unselectedCardStyles)}

  @media (max-width: 768px) {
    width: 105px;
    height: 105px;
  }

  @media (max-width: 480px) {
    width: 85px;
    height: 85px;
    border-radius: 12px;
  }
`;

export const CardImage = styled.img`
  width: 50%;
  height: 50%;
  object-fit: contain;
  border-radius: 10px;
  pointer-events: none;
`;

export const CardGradientThumb = styled.div`
  width: 50%;
  height: 50%;
  border-radius: 10px;
  background: ${(props) => props.gradient || "#1a1a1a"};
  pointer-events: none;
`;

export const CardTitle = styled.span`
  font-family: Comfortaa;
  font-size: 10px;
  font-weight: 400;
  color: white;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
  pointer-events: none;

  @media (max-width: 480px) {
    font-size: 8px;
  }
`;
